<script>
	import { onMount } from 'svelte';
	import { injectGlobalStyles } from './shared.js';
	import { getStoredAuthorization, storeAuthorization, clearAuthorization, apiRequest } from './auth.js';
	import AuthDialog from './AuthDialog.svelte';
	import Loader from './Loader.svelte';
	import TabPane from './TabPane.svelte';
	import TabView from './TabView.svelte';
	import WidgetSelect from './WidgetSelect.svelte';

	export let apiUrl = '';

	let host;
	let authorized = false;
	let authorization = null;
	let profile = emptyProfile();
	let profileLoading = false;
	let savingProfile = false;
	let errorMessage = '';
	let successMessage = '';

	// История ставок пользователя
	let myBets = [];
	let myBetsLoading = false;
	let myBetsError = '';

	$: myBetsStats = computeMyBetsStats(myBets);
	$: myBetsSorted = sortMyBets(myBets);

	onMount(() => {
		const root = host?.getRootNode();
		if (root instanceof ShadowRoot) injectGlobalStyles(root);
		void restoreSession();
	});

	function emptyProfile() {
		return {
			firstName: '',
			lastName: '',
			surName: '',
			birthday: '',
			gender: 0,
			phone: ''
		};
	}

	async function restoreSession() {
		const stored = getStoredAuthorization();
		// Если пользователь уже полностью авторизован — показываем профиль.
		// В противном случае (нет сессии или нужна регистрация) AuthDialog разберётся сам.
		if (stored && !stored.newUser) {
			authorization = stored;
			authorized = true;
			await loadProfile();
			void loadMyBets();
		}
	}

	function handleAuthorized(event) {
		authorization = event.detail.authorization;
		authorized = true;
		errorMessage = '';
		profile = profileFromUser(authorization.user);
		void loadProfile();
		void loadMyBets();
	}

	function clearSession() {
		clearAuthorization();
		authorization = null;
		authorized = false;
		profile = emptyProfile();
		errorMessage = '';
		successMessage = '';
	}

	function profileFromUser(user) {
		return {
			firstName: user?.firstName || '',
			lastName: user?.lastName || '',
			surName: user?.surName || '',
			birthday: dateInputValue(user?.birthday),
			gender: Number(user?.gender) || 0,
			phone: user?.phone || ''
		};
	}

	function dateInputValue(value) {
		if (!value) return '';
		if (/^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);
		const date = new Date(value);
		if (Number.isNaN(date.getTime())) return '';
		return date.toISOString().slice(0, 10);
	}

	function validateBirthDate(value) {
		if (!value) return '';
		const date = new Date(`${value}T00:00:00`);
		if (Number.isNaN(date.getTime())) return 'Укажите корректную дату рождения.';
		if (date > new Date()) return 'Дата рождения не может быть в будущем.';
		return '';
	}

	async function loadProfile() {
		if (!authorization?.accessToken) return;
		profileLoading = true;
		errorMessage = '';
		try {
			const user = await apiRequest('profile', {
				accessToken: authorization.accessToken,
				baseUrl: apiUrl
			});
			profile = profileFromUser(user);
			authorization = { ...authorization, user, newUser: false };
			storeAuthorization(authorization);
		} catch (error) {
			if (error.status === 401) {
				clearSession();
				errorMessage = 'Сессия завершена. Войдите снова.';
			} else {
				errorMessage = error.message;
			}
		} finally {
			profileLoading = false;
		}
	}

	async function updateProfile() {
		errorMessage = '';
		successMessage = '';
		if (!profile.lastName.trim() || !profile.firstName.trim()) {
			errorMessage = 'Заполните имя и фамилию.';
			return;
		}
		if (profile.birthday) {
			const birthDateError = validateBirthDate(profile.birthday);
			if (birthDateError) {
				errorMessage = birthDateError;
				return;
			}
		}

		savingProfile = true;
		try {
			const user = await apiRequest('profile', {
				method: 'POST',
				accessToken: authorization.accessToken,
				baseUrl: apiUrl,
				body: {
					firstName: profile.firstName.trim(),
					lastName: profile.lastName.trim(),
					surName: profile.surName.trim() || null,
					birthday: profile.birthday ? `${profile.birthday}T00:00:00.000Z` : null,
					gender: Number(profile.gender) || 0
				}
			});
			profile = profileFromUser(user);
			authorization = { ...authorization, user };
			storeAuthorization(authorization);
			successMessage = 'Профиль сохранён.';
		} catch (error) {
			if (error.status === 401) {
				clearSession();
				errorMessage = 'Сессия завершена. Войдите снова.';
			} else {
				errorMessage = error.message;
			}
		} finally {
			savingProfile = false;
		}
	}

	async function loadMyBets() {
		if (!authorization?.accessToken) return;
		myBetsLoading = true;
		myBetsError = '';
		try {
			const payload = await apiRequest('betting/my-bets', {
				accessToken: authorization.accessToken,
				baseUrl: apiUrl
			});
			myBets = Array.isArray(payload) ? payload : payload?.$values || payload?.items || [];
		} catch (error) {
			console.error('Load my bets error:', error);
			myBetsError = error.message || 'Не удалось загрузить историю ставок';
		} finally {
			myBetsLoading = false;
		}
	}

	function computeMyBetsStats(bets) {
		const list = bets || [];
		const wins = list.filter((item) => item?.bet?.isWin === true).length;
		const losses = list.filter((item) => item?.bet?.isLost === true).length;
		const total = list.length;
		const resolved = wins + losses;
		const percent = resolved > 0 ? Math.round((wins / resolved) * 100) : 0;
		return { total, wins, losses, percent };
	}

	function sortMyBets(bets) {
		return [...(bets || [])].sort((a, b) => {
			const da = betTimestamp(a);
			const db = betTimestamp(b);
			return da - db;
		});
	}

	function betTimestamp(item) {
		const value = item?.betting?.bettingDate ?? item?.bet?.bettingDate;
		const date = value ? new Date(value) : null;
		return date && !Number.isNaN(date.getTime()) ? date.getTime() : 0;
	}

	function formatBetDate(value) {
		if (!value) return '—';
		const date = new Date(value);
		if (Number.isNaN(date.getTime())) return '—';
		const day = String(date.getDate()).padStart(2, '0');
		const month = String(date.getMonth() + 1).padStart(2, '0');
		return `${day}.${month}.${date.getFullYear()}`;
	}

	function betTeamName(item) {
		const bet = item?.bet?.bet;
		if (bet === 1) return item?.betting?.result1Name || 'Команда 1';
		if (bet === 2) return item?.betting?.result2Name || 'Команда 2';
		return '—';
	}

	function betStatus(item) {
		const bet = item?.bet;
		if (bet?.isWin === true) return { label: 'Выигрыш', cls: '--win' };
		if (bet?.isLost === true) return { label: 'Проигрыш', cls: '--lose' };
		return { label: 'Ожидает', cls: '--pending' };
	}
</script>

<section class="account" bind:this={host}>
	{#if authorized}
		<header class="profile-header">
			<div>
				<p class="eyebrow">Личный кабинет</p>
				<h1>Личный кабинет</h1>
			</div>
			<div class="profile-actions">
				<span class="auth-status"><span></span>Вы авторизованы</span>
				<button class="secondary-button logout-button" type="button" on:click={clearSession}>Выйти</button>
			</div>
		</header>
		{#if errorMessage}<p class="message error" role="alert">{errorMessage}</p>{/if}
		{#if successMessage}<p class="message success" role="status">{successMessage}</p>{/if}
		<TabView align="left">
			<TabPane title="Профиль">
				{#if profileLoading}
					<div class="loading-state" role="status"><Loader size="48px" />Загружаем профиль…</div>
				{:else}
					<form class="profile-form" on:submit|preventDefault={updateProfile}>
						<div class="form-grid">
							<div>
								<label for="profile-phone">Телефон</label>
								<input id="profile-phone" value={profile.phone} readonly />
							</div>
							<div>
								<label for="profile-gender">Пол</label>
								<WidgetSelect
									className="--black"
									name="profile-gender"
									options={[
										{ value: '0', label: 'Не указан' },
										{ value: '1', label: 'Мужской' },
										{ value: '2', label: 'Женский' }
									]}
									bind:value={profile.gender}
								/>
							</div>
							<div>
								<label for="profile-last-name">Фамилия <span>*</span></label>
								<input id="profile-last-name" autocomplete="family-name" bind:value={profile.lastName} required />
							</div>
							<div>
								<label for="profile-first-name">Имя <span>*</span></label>
								<input id="profile-first-name" autocomplete="given-name" bind:value={profile.firstName} required />
							</div>
							<div>
								<label for="profile-sur-name">Отчество</label>
								<input id="profile-sur-name" bind:value={profile.surName} />
							</div>
							<div>
								<label for="profile-birthday">Дата рождения</label>
								<input id="profile-birthday" type="date" max={new Date().toISOString().slice(0, 10)} bind:value={profile.birthday} />
							</div>
						</div>
						<button class="primary-button save-button" type="submit" disabled={savingProfile}>
							{savingProfile ? 'Сохраняем…' : 'Сохранить изменения'}
						</button>
					</form>
				{/if}
			</TabPane>
			<TabPane title="Конкурс прогнозов">
				<div class="predictions-stats">
					<div class="predictions-stat">
						<span class="predictions-stat__value">{myBetsStats.total}</span>
						<span class="predictions-stat__label">Ставок всего</span>
					</div>
					<div class="predictions-stat">
						<span class="predictions-stat__value --win">{myBetsStats.wins}</span>
						<span class="predictions-stat__label">Выигрышей</span>
					</div>
					<div class="predictions-stat">
						<span class="predictions-stat__value --lose">{myBetsStats.losses}</span>
						<span class="predictions-stat__label">Проигрышей</span>
					</div>
					<div class="predictions-stat">
						<span class="predictions-stat__value">{myBetsStats.percent}%</span>
						<span class="predictions-stat__label">Процент</span>
					</div>
				</div>

				<div class="predictions-history">
					<h3 class="predictions-history__title">История ставок</h3>

					{#if myBetsLoading}
						<div class="loading-state" role="status"><Loader size="48px" />Загружаем ставки…</div>
					{:else if myBetsError}
						<p class="message error" role="alert">{myBetsError}</p>
					{:else if !myBetsSorted.length}
						<div class="empty-predictions">
							<div class="empty-icon" aria-hidden="true">🏀</div>
							<h2>Ставок пока нет</h2>
							<p>Делайте прогнозы на матчи, и они появятся здесь.</p>
						</div>
					{:else}
						<div class="table__wrap">
							<table class="table">
								<thead>
									<tr>
										<th>Дата</th>
										<th>Матч</th>
										<th>Ставка</th>
										<th>Результат</th>
									</tr>
								</thead>
								<tbody>
									{#each myBetsSorted as item}
										{@const status = betStatus(item)}
										<tr>
											<td>{formatBetDate(item?.betting?.bettingDate)}</td>
											<td>{item?.betting?.result1Name || '—'} — {item?.betting?.result2Name || '—'}</td>
											<td>{betTeamName(item)}</td>
											<td>
												<span class="bet-status {status.cls}">{status.label}</span>
											</td>
										</tr>
									{/each}
								</tbody>
							</table>
						</div>
					{/if}
				</div>
			</TabPane>
		</TabView>
	{:else}
		<AuthDialog inline {apiUrl} intro="Войдите по номеру телефона, чтобы открыть профиль и участвовать в конкурсах прогнозов." on:authorized={handleAuthorized} />
	{/if}
</section>

<style>
	:host {
		display: block;
		color: #122033;
		font-family: inherit;
	}

	.account {
		--account-primary: #00244d;
		--account-muted: #62748a;
		width: min(100%, 900px);
		margin: 0 auto;
	}

	h1,
	h2,
	p {
		margin-top: 0;
	}

	h1 {
		margin-bottom: 12px;
		color: var(--account-primary);
		font-size: clamp(24px, 4vw, 32px);
		line-height: 1.2;
	}

	.eyebrow {
		margin-bottom: 8px;
		color: #71839a;
		font-size: 12px;
		font-weight: 700;
		letter-spacing: 0.1em;
		text-transform: uppercase;
	}

	label {
		margin-top: 6px;
		color: #33465d;
		font-size: 13px;
		font-weight: 650;
	}

	label span {
		color: #c34646;
	}

	input,
	select {
		box-sizing: border-box;
		width: 100%;
		min-height: 46px;
		padding: 11px 13px;
		border: 1px solid #d6dee8;
		border-radius: 10px;
		outline: none;
		background: #fff;
		color: #122033;
		font: inherit;
		font-size: 14px;
		transition: border-color 150ms ease, box-shadow 150ms ease;
	}

	input:focus,
	select:focus {
		border-color: #24598e;
		box-shadow: 0 0 0 3px rgb(36 89 142 / 12%);
	}

	input[readonly] {
		background: #f4f6f9;
		color: #62748a;
	}

	button {
		font: inherit;
	}

	.primary-button,
	.secondary-button {
		min-height: 44px;
		padding: 10px 16px;
		border-radius: 10px;
		font-size: 14px;
		font-weight: 650;
		cursor: pointer;
		transition: background 150ms ease, border-color 150ms ease, opacity 150ms ease;
	}

	.primary-button {
		margin-top: 8px;
		border: 1px solid var(--account-primary);
		background: var(--account-primary);
		color: #fff;
	}

	.primary-button:hover:not(:disabled) {
		background: #123e6d;
	}

	.primary-button:disabled {
		cursor: not-allowed;
		opacity: 0.55;
	}

	.secondary-button {
		border: 1px solid #d6dee8;
		background: #fff;
		color: var(--account-primary);
	}

	.secondary-button:hover {
		border-color: #9eafc2;
		background: #f7f9fc;
	}

	.loading-state {
		display: flex;
		min-height: 130px;
		align-items: center;
		justify-content: center;
		gap: 12px;
		color: var(--account-muted);
		font-size: 14px;
	}

	.message {
		margin: 12px 0;
		padding: 12px 14px;
		border-radius: 10px;
		font-size: 13px;
		line-height: 1.5;
	}

	.error {
		border: 1px solid #f0caca;
		background: #fff5f5;
		color: #a52f2f;
	}

	.success {
		border: 1px solid #c9e8d7;
		background: #f1fbf5;
		color: #267247;
	}

	.profile-header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 16px;
	}

	.profile-header h1 {
		margin-bottom: 0;
	}

	.logout-button {
		flex: 0 0 auto;
	}

	.profile-actions {
		display: flex;
		align-items: center;
		gap: 14px;
	}

	.auth-status {
		display: inline-flex;
		align-items: center;
		gap: 7px;
		color: #267247;
		font-size: 13px;
		font-weight: 650;
		white-space: nowrap;
	}

	.auth-status span {
		width: 8px;
		height: 8px;
		border-radius: 50%;
		background: #2e9a5f;
		box-shadow: 0 0 0 3px rgb(46 154 95 / 13%);
	}

	.form-grid {
		display: grid;
		grid-template-columns: repeat(2, minmax(0, 1fr));
		gap: 14px 18px;
	}

	.form-grid > div {
		display: flex;
		min-width: 0;
		flex-direction: column;
		gap: 7px;
	}

	.save-button {
		width: fit-content;
		min-width: 190px;
	}

	.empty-predictions {
		padding: 42px 20px;
		border: 1px dashed #d6dee8;
		border-radius: 14px;
		background: #fbfcfe;
		text-align: center;
	}

	.empty-icon {
		margin-bottom: 12px;
		font-size: 34px;
	}

	.empty-predictions h2 {
		margin-bottom: 8px;
		color: var(--account-primary);
		font-size: 19px;
	}

	.empty-predictions p {
		margin-bottom: 0;
		color: var(--account-muted);
		font-size: 14px;
	}

	.predictions-stats {
		display: grid;
		grid-template-columns: repeat(4, minmax(0, 1fr));
		gap: 12px;
		margin-bottom: 24px;
	}

	.predictions-stat {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 4px;
		padding: 18px 12px;
		border: 1px solid #e3e8ef;
		border-radius: 12px;
		background: #fff;
	}

	.predictions-stat__value {
		font-size: 28px;
		font-weight: 800;
		line-height: 1;
		color: #122033;
	}

	.predictions-stat__value.--win {
		color: #16a34a;
	}

	.predictions-stat__value.--lose {
		color: #ca2319;
	}

	.predictions-stat__label {
		font-size: 12px;
		color: #6b7a90;
	}

	.predictions-history__title {
		margin: 0 0 12px;
		font-size: 18px;
		font-weight: 700;
		color: #122033;
	}

	.bet-status {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		padding: 3px 10px;
		border-radius: 999px;
		font-size: 12px;
		font-weight: 700;
		white-space: nowrap;
	}

	.bet-status.--win {
		background: #e8f7ef;
		color: #1a7a45;
	}

	.bet-status.--lose {
		background: #fdecec;
		color: #b02a2a;
	}

	.bet-status.--pending {
		background: #eef2f7;
		color: #53657b;
	}

	@media (max-width: 560px) {
		.profile-header,
		.profile-actions {
			align-items: flex-start;
		}

		.profile-header {
			flex-direction: column;
		}

		.form-grid {
			grid-template-columns: 1fr;
		}

		.predictions-stats {
			grid-template-columns: repeat(2, minmax(0, 1fr));
		}
	}
</style>
